import { createContext, useContext, type ReactNode } from 'react';
import type { AppConfig } from '@cqp/contracts';

const ConfigContext = createContext<AppConfig | null>(null);

/**
 * Aplica a marca às variáveis CSS.
 *
 * É aqui que cor e fonte entram na página — nenhum valor da Qualimplan está
 * escrito no CSS nem em componente (Guia §8).
 */
export function applyBrand(config: AppConfig): void {
  const root = document.documentElement.style;
  const { colors, fonts } = config;

  /**
   * Uma entrada por cor, explícita.
   *
   * Um laço sobre `Object.entries` seria mais curto e calaria o caso que importa:
   * o nome da variável CSS é contrato com a folha de estilo, e derivá-lo do nome
   * do campo faria um `rename` no contrato apagar uma cor da tela em silêncio.
   */
  root.setProperty('--brand-primary', colors.primary);
  root.setProperty('--brand-secondary', colors.secondary);
  root.setProperty('--brand-secondary-lift', colors.secondaryLift);
  root.setProperty('--brand-secondary-light', colors.secondaryLight);
  root.setProperty('--brand-neutral', colors.neutral);
  root.setProperty('--brand-detail', colors.detail);
  root.setProperty('--brand-severity-normal', colors.severityNormal);
  root.setProperty('--brand-severity-attention', colors.severityAttention);
  root.setProperty('--brand-severity-critical', colors.severityCritical);
  root.setProperty('--brand-ink', colors.ink);
  root.setProperty('--brand-ink-soft', colors.inkSoft);
  root.setProperty('--brand-ink-muted', colors.inkMuted);
  root.setProperty('--brand-font-display', fonts.display);
  root.setProperty('--brand-font-text', fonts.text);
  document.title = `${config.clinicName} — Painel de Atendimento`;
}

export function ConfigProvider({
  config,
  children,
}: {
  readonly config: AppConfig;
  readonly children: ReactNode;
}): ReactNode {
  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}

export function useConfig(): AppConfig {
  const config = useContext(ConfigContext);
  // Erro de programação, não de runtime: o provider envolve a aplicação inteira.
  if (config === null) throw new Error('useConfig fora de ConfigProvider');
  return config;
}
